import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Drawer,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  AttachMoney,
  Assignment,
  CheckCircle,
  Schedule,
  Receipt,
  Star,
  Business,
  Visibility,
  TrendingUp as TrendingUpIcon,
  Download,
  CalendarMonth,
  Refresh,
  PictureAsPdf,
  InsertDriveFile,
  MoreVert,
  ShoppingBag,
} from '@mui/icons-material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import '../../css/admin/admin.css';

import {
  useGetDashboardAnalyticsQuery,
  useGetMostViewedProductsQuery,
  useGetHighSaleProductsQuery,
  useDownloadMostViewedPDFMutation,
  useDownloadHighSalePDFMutation,
  useDownloadMostViewedCSVMutation,
  useDownloadHighSaleCSVMutation,
  RangeType,
  MostViewedProduct,
  HighSaleProduct,
} from '../../features/admin/dashboardApi.ts';

const RANGE_OPTIONS: { value: RangeType; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom' },
];


const PRODUCT_RANGE_OPTIONS = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

// Alert component for Snackbar
const AlertComponent = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatNumber = (value?: number | string) => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN');
  }
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toLocaleString('en-IN');
  }
  return '0';
};

const formatCurrency = (value?: number | string) => {
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? `${value} AED` : `${num.toLocaleString('en-IN')} AED`;
  }
  return `${formatNumber(value)} AED`;
};

const formatDecimal = (value?: number | string) => {
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? value : num.toFixed(2);
  }
  return '0.00';
};

const calcPercent = (current?: number, last?: number): number | null => {
  if (last === undefined || last === 0 || current === undefined) return null;
  return ((current - last) / last) * 100;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rangeType, setRangeType] = useState<RangeType>('last_week');
  const [productTab, setProductTab] = useState(0);
  const [productRange, setProductRange] = useState<'week' | 'month' | 'year' | 'custom'>('week');
  const [customDateDialog, setCustomDateDialog] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);

  // Dashboard analytics query
  const { 
    data, 
    error: dashboardError, 
    isLoading: dashboardLoading, 
    isFetching: dashboardFetching,
    refetch: refetchDashboard 
  } = useGetDashboardAnalyticsQuery(rangeType);

  // Product analytics queries
  const {
    data: mostViewedData,
    isLoading: mostViewedLoading,
    error: mostViewedError,
    refetch: refetchMostViewed,
  } = useGetMostViewedProductsQuery({
    range: productRange,
    ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
    pdf: 0,
    csv: 0,
  });

  const {
    data: highSaleData,
    isLoading: highSaleLoading,
    error: highSaleError,
    refetch: refetchHighSale,
  } = useGetHighSaleProductsQuery({
    range: productRange,
    ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
    pdf: 0,
    csv: 0,
  });

  // Download mutations
  const [downloadMostViewedPDF] = useDownloadMostViewedPDFMutation();
  const [downloadHighSalePDF] = useDownloadHighSalePDFMutation();
  const [downloadMostViewedCSV] = useDownloadMostViewedCSVMutation();
  const [downloadHighSaleCSV] = useDownloadHighSaleCSVMutation();

  useEffect(() => {
    // Reset dates when range changes
    if (productRange !== 'custom') {
      setFromDate('');
      setToDate('');
    }
  }, [productRange]);

  const handleToggleMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRangeChange = (
    event: SelectChangeEvent<RangeType>
  ) => {
    const newRange = event.target.value as RangeType;
    setRangeType(newRange);
  };

  const handleProductRangeChange = (
    event: SelectChangeEvent<'week' | 'month' | 'year' | 'custom'>
  ) => {
    const value = event.target.value;
    setProductRange(value);
    if (value === 'custom') {
      setCustomDateDialog(true);
    }
  };

  const handleProductTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setProductTab(newValue);
  };

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async (type: 'pdf' | 'csv') => {
    try {
      setDownloading(true);
      const params = {
        range: productRange,
        ...(productRange === 'custom' && fromDate && toDate ? { from_date: fromDate, to_date: toDate } : {}),
      };

      let result: any;
      
      if (productTab === 0) {
        // Most Viewed Products
        if (type === 'pdf') {
          result = await downloadMostViewedPDF(params).unwrap();
        } else {
          const blob = await downloadMostViewedCSV(params).unwrap();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `most-viewed-products-${productRange}-${new Date().toISOString().split('T')[0]}.${type}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      } else {
        // High Sale Products
        if (type === 'pdf') {
          result = await downloadHighSalePDF(params).unwrap();
        } else {
          const blob = await downloadHighSaleCSV(params).unwrap();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `high-sale-products-${productRange}-${new Date().toISOString().split('T')[0]}.${type}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }

      // For PDF downloads, open the URL
      if (type === 'pdf' && result?.download_url) {
        window.open(result.download_url, '_blank');
      }

      setSnackbar({
        open: true,
        message: type === 'pdf' 
          ? 'PDF generated successfully! Opening download...' 
          : 'CSV downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error downloading ${type.toUpperCase()}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to download ${type.toUpperCase()}. Please try again.`,
        severity: 'error'
      });
    } finally {
      setDownloading(false);
      handleDownloadMenuClose();
    }
  };

  const handleApplyCustomDates = () => {
    if (fromDate && toDate) {
      if (productTab === 0) {
        refetchMostViewed();
      } else {
        refetchHighSale();
      }
      setCustomDateDialog(false);
      setSnackbar({
        open: true,
        message: `Filter applied: ${fromDate} to ${toDate}`,
        severity: 'info'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Please select both from and to dates',
        severity: 'warning'
      });
    }
  };

  const handleRefreshData = () => {
    if (productTab === 0) {
      refetchMostViewed();
    } else {
      refetchHighSale();
    }
    setSnackbar({
      open: true,
      message: 'Data refreshed!',
      severity: 'info'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Safely read values from API
  const dailyTotalSales = data?.daily_sales?.total?.sales ?? 0;
  const dailyTotalOrders = data?.daily_sales?.total?.orders ?? 0;

  const weeklyNewUsersCurrent = data?.new_users?.total?.current_week ?? 0;
  const weeklyNewUsersLast = data?.new_users?.total?.last_week ?? 0;
  const weeklyNewUsersPercent = calcPercent(
    weeklyNewUsersCurrent,
    weeklyNewUsersLast
  );

  const rangeSalesCurrent = data?.range?.range_summary?.total?.sales ?? 0;
  const rangeSalesPercent =
    data?.range?.comparison?.total?.sales?.percent ?? null;

  const monthCurrent = data?.month_growth?.total?.current ?? 0;
  const monthLast = data?.month_growth?.total?.last ?? 0;
  const monthPercent = calcPercent(monthCurrent, monthLast);

  const quarterCurrent = data?.quarter_growth?.total?.current ?? 0;
  const quarterLast = data?.quarter_growth?.total?.last ?? 0;
  const quarterPercent = calcPercent(quarterCurrent, quarterLast);

  // Current data based on active tab
  const currentData = productTab === 0 
    ? mostViewedData?.data || [] 
    : highSaleData?.data || [];

  // Table headers based on active tab
  const mostViewedHeaders = [
    { key: 'product_id', label: 'Product ID' },
    { key: 'product_name', label: 'Product Name' },
    { key: 'price', label: 'Price (AED)' },
    { key: 'total_views', label: 'Total Views' },
    { key: 'total_viewers', label: 'Total Viewers' },
  ];

  const highSaleHeaders = [
    { key: 'product_id', label: 'Product ID' },
    { key: 'product_name', label: 'Product Name' },
    { key: 'price', label: 'Price (AED)' },
    { key: 'total_qty', label: 'Total Quantity' },
    { key: 'total_buyers', label: 'Total Buyers' },
    { key: 'avg_sale_per_day', label: 'Average Sales/Day' },
  ];

  const currentHeaders = productTab === 0 ? mostViewedHeaders : highSaleHeaders;

  // 4 top stat cards (now dynamic)
  const statsCards = [
    {
      title: "Today's Sales",
      value: formatCurrency(dailyTotalSales),
      icon: <Assignment />,
      accent: '#FF5A00',
      progress: 100,
    },
    {
      title: "Today's Orders",
      value: formatNumber(dailyTotalOrders),
      icon: <CheckCircle />,
      accent: '#FF5A00',
      progress: 100,
    },
    {
      title: 'New Users This Week',
      value: formatNumber(weeklyNewUsersCurrent),
      icon: <Schedule />,
      accent: '#FF5A00',
      progress: 100,
    },
    {
      title: 'User Growth vs Last Week',
      value: `${
        weeklyNewUsersPercent !== null
          ? (weeklyNewUsersPercent > 0 ? '+' : '') + weeklyNewUsersPercent.toFixed(1)
          : '0'
      }%`,
      icon: <Receipt />,
      accent: '#FF5A00',
      progress: 100,
    },
  ];

  // 3 sales cards (dynamic)
  const salesCards = [
    {
      title: 'Sales for Selected Period',
      value: formatCurrency(rangeSalesCurrent),
      subtitle: 'Total sales for the selected period',
      icon: <TrendingUp />,
      accent: '#FF5A00',
      change:
        rangeSalesPercent !== null
          ? (rangeSalesPercent > 0 ? '+' : '') + rangeSalesPercent.toFixed(1) + '%'
          : '0%',
      changeColor: rangeSalesPercent !== null && rangeSalesPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(monthCurrent),
      subtitle: `Previous month: ${formatCurrency(monthLast)}`,
      icon: <AttachMoney />,
      accent: '#FF5A00',
      change:
        monthPercent !== null ? (monthPercent > 0 ? '+' : '') + monthPercent.toFixed(1) + '%' : '0%',
      changeColor: monthPercent !== null && monthPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
    {
      title: 'Quarter Revenue',
      value: formatCurrency(quarterCurrent),
      subtitle: `Previous quarter: ${formatCurrency(quarterLast)}`,
      icon: <ShoppingCart />,
      accent: '#FF5A00',
      change:
        quarterPercent !== null
          ? (quarterPercent > 0 ? '+' : '') + quarterPercent.toFixed(1) + '%'
          : '0%',
      changeColor: quarterPercent !== null && quarterPercent >= 0 ? '#4CAF50' : '#FF6B6B'
    },
    {
      title: 'Average Order Value',
      value: formatCurrency(dailyTotalOrders > 0 ? dailyTotalSales / dailyTotalOrders : 0),
      subtitle: "Based on today's sales and orders",
      icon: <ShoppingBag />,
      accent: '#FF5A00',
      change: '—',
      changeColor: '#667085',
    },
  ];

  const topProducts =
    data?.top_products?.slice(0, 5).map((p: any, index: number) => ({
      name: p.product_name || `Product #${p.product_id}`,
      quantity: p.total_quantity,
      value: p.total_value,
      rank: index + 1,
    })) ?? [];

  const currentRangeLabel =
    RANGE_OPTIONS.find((r) => r.value === rangeType)?.label || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <AlertComponent onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </AlertComponent>
      </Snackbar>

      {/* Download Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={() => handleDownload('pdf')} disabled={downloading}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDownload('csv')} disabled={downloading}>
          <ListItemIcon>
            <InsertDriveFile fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
      </Menu>

      {/* Custom Date Dialog */}
      <Dialog open={customDateDialog} onClose={() => setCustomDateDialog(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, minWidth: 300 }}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDateDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyCustomDates} variant="contained" sx={{ color: '#FF5A00', borderColor: '#FFB38A' }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row: Sidebar + Main Content */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        <Box
          sx={{
            width: '280px',
            flexShrink: 0,
            overflowY: 'auto',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box sx={{ width: '100%', height: '100%' }}>
            <Slidemenu
              mobileOpen={false}
              onToggle={handleToggleMenu}
            />
          </Box>
        </Box>

        {/* Mobile Sidebar */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleToggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu
            mobileOpen={mobileOpen}
            onToggle={handleToggleMenu}
          />
        </Drawer>

        {/* Main Content area with Header + Content */}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ flexShrink: 0 }}>
            <AdminHeader onToggleMenu={handleToggleMenu} />
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              padding: { xs: 1, sm: 2.5 },
              paddingTop: '8px',
              background: '#F8F9FB',
              minHeight: '100vh',
            }}
          >
            <Box
              sx={{
                padding: { xs: 1, sm: 1.5 },
                width: '100%',
                maxWidth: 'none',
                margin: 0,
              }}
            >
              {/* Dashboard Header + Range Filter */}
              <Box
                sx={{
                  mb: 2.5,
                  background: '#FFFFFF',
                  borderRadius: 2,
                  padding: { xs: 2, sm: 2.5 },
                  color: '#111827',
                  border: '1px solid #E8EAEE',
                  boxShadow: '0 2px 10px rgba(16, 24, 40, 0.04)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '24px', sm: '28px' },
                      mb: 0.5,
                    }}
                  >
                    Welcome back, Admin 👋
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#667085' }}>
                    {`Here's what's happening with your store.`}
                  </Typography>
                </Box>

                <FormControl
                  size="small"
                  sx={{ minWidth: 160, backgroundColor: '#fff', borderRadius: 1.5 }}
                >
                  <InputLabel id="range-type-label">Date Range</InputLabel>
                  <Select
                    labelId="range-type-label"
                    value={rangeType}
                    label="Date Range"
                    onChange={handleRangeChange}
                  >
                    {RANGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Loading / Error State for Dashboard */}
              {dashboardLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 5,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {dashboardError && !dashboardLoading && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">
                    Failed to load dashboard analytics. Please check your connection.
                  </Alert>
                </Box>
              )}

              {!dashboardLoading && (
                <>
                  {dashboardFetching && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress />
                    </Box>
                  )}

                  {/* Sales Overview */}
                  <Card sx={{ minHeight: 300, border: '1px solid #E8EAEE', borderRadius: 2, boxShadow: '0 2px 10px rgba(16,24,40,0.04)', mb: 2.5 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: '#344054' }}>Sales Overview</Typography>
                      <Box sx={{ position: 'relative', height: 220, mt: 1, borderLeft: '1px solid #EAECF0', borderBottom: '1px solid #EAECF0' }}>
                        {[0, 1, 2, 3].map((line) => <Box key={line} sx={{ position: 'absolute', left: 0, right: 0, bottom: `${line * 33.33}%`, borderTop: '1px dashed #EAECF0' }} />)}
                        <svg viewBox="0 0 700 220" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                          <defs><linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF5A00" stopOpacity="0.18" /><stop offset="100%" stopColor="#FF5A00" stopOpacity="0.02" /></linearGradient></defs>
                          <path d="M0 165 L116 82 L233 150 L350 160 L466 178 L583 184 L700 112 L700 220 L0 220 Z" fill="url(#salesFill)" />
                          <path d="M0 165 L116 82 L233 150 L350 160 L466 178 L583 184 L700 112" fill="none" stroke="#FF5A00" strokeWidth="3" />
                          {[['0','165'],['116','82'],['233','150'],['350','160'],['466','178'],['583','184'],['700','112']].map(([x,y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="4" fill="#FF5A00" />)}
                        </svg>
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -24, display: 'flex', justifyContent: 'space-between', color: '#667085', fontSize: 11 }}>
                          {['12 May','13 May','14 May','15 May','16 May','17 May','18 May'].map((day) => <span key={day}>{day}</span>)}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Stats Cards Row (4) */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 2,
                      width: '100%',
                      mb: 2.5,
                      alignItems: 'stretch',
                    }}
                  >
                    {statsCards.map((card, index) => (
                      <Box key={index} sx={{ minWidth: 0, display: 'flex' }}>
                        <Card
                          sx={{
                            background: '#FFFFFF',
                            color: '#111827',
                            borderRadius: 2,
                            border: '1px solid #E8EAEE',
                            boxShadow: '0 2px 10px rgba(16, 24, 40, 0.05)',
                            height: { xs: 118, lg: 126 },
                            width: '100%',
                          }}
                        >
                          <CardContent sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: '#FFF1E8',
                                  color: '#FF5A00',
                                  mr: 1.5,
                                  width: 42,
                                  height: 42,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Box>
                                <Typography
                                  sx={{ fontWeight: 400, fontSize: 18, lineHeight: 1.3, color: '#344054' }}
                                >
                                  {card.value}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#667085', fontSize: 12, fontWeight: 400, lineHeight: 1.3 }}
                                >
                                  {card.title}
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={card.progress}
                              sx={{
                                bgcolor: '#F2F4F7',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#ed9667',
                                },
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>

                  {/* Sales Cards Row (4) */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 2,
                      width: '100%',
                      mb: 2.5,
                      alignItems: 'stretch',
                    }}
                  >
                    {salesCards.map((card, index) => (
                      <Box key={index} sx={{ minWidth: 0, display: 'flex' }}>
                        <Card
                          sx={{
                            background: '#FFFFFF',
                            color: '#111827',
                            borderRadius: 2,
                            border: '1px solid #E8EAEE',
                            boxShadow: '0 2px 10px rgba(16, 24, 40, 0.05)',
                            height: { xs: 148, lg: 150 },
                            width: '100%',
                          }}
                        >
                          <CardContent
                            sx={{
                              p: 2,
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: '#FFF1E8',
                                  color: '#FF5A00',
                                  width: 42,
                                  height: 42,
                                }}
                              >
                                {card.icon}
                              </Avatar>
                              <Chip
                                label={card.change}
                                size="small"
                                sx={{
                                  bgcolor: '#FFF1E8',
                                  color: card.changeColor || '#667085',
                                  fontWeight: 400,
                                  fontSize: 11,
                                  borderColor: card.changeColor,
                                  borderWidth: 1,
                                  borderStyle: 'solid',
                                }}
                              />
                            </Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 400, fontSize: 16, mb: 0.5, color: '#344054', lineHeight: 1.35 }}
                            >
                              {card.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#667085', fontWeight: 400, fontSize: 13, mb: 0.5, lineHeight: 1.35 }}
                            >
                              {card.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#667085' }}>
                              {card.subtitle}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </Box>

                  {/* Main Analytics Area: 2/3 Product Analytics + 1/3 Quick Stats/Sales Overview */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(320px, 1fr)' },
                      gap: 2,
                      width: '100%',
                      alignItems: 'stretch',
                    }}
                  >
                    {/* Product Analytics */}
                    <Card
                      sx={{
                        minWidth: 0,
                        border: '1px solid #E8EAEE',
                        borderRadius: 2,
                        boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                        overflow: 'hidden',
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Box
                          sx={{
                            px: 2.5,
                            pt: 2.25,
                            pb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ color: '#344054', fontWeight: 500 }}>
                              Product Analytics
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#667085' }}>
                              Performance by product for the selected period.
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                              <InputLabel id="product-range-label">Product Range</InputLabel>
                              <Select
                                labelId="product-range-label"
                                value={productRange}
                                label="Product Range"
                                onChange={handleProductRangeChange}
                              >
                                {PRODUCT_RANGE_OPTIONS.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <IconButton size="small" onClick={() => window.location.reload()}>
                              <Refresh fontSize="small" />
                            </IconButton>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Download />}
                              onClick={handleDownloadMenuOpen}
                              disabled={downloading}
                              sx={{
                                color: '#FF5A00',
                                borderColor: '#FFB38A',
                                textTransform: 'uppercase',
                                fontWeight: 700,
                                '&:hover': { borderColor: '#FF5A00', backgroundColor: '#FFF7F2' },
                              }}
                            >
                              Download
                            </Button>
                            <IconButton size="small" onClick={handleDownloadMenuOpen}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Tabs
                          value={productTab}
                          onChange={handleProductTabChange}
                          sx={{
                            px: 2.5,
                            minHeight: 42,
                            borderBottom: '1px solid #EAECF0',
                            '& .MuiTab-root': {
                              minHeight: 42,
                              textTransform: 'none',
                              fontWeight: 600,
                              color: '#667085',
                              px: 1.5,
                            },
                            '& .Mui-selected': { color: '#FF5A00 !important' },
                            '& .MuiTabs-indicator': { backgroundColor: '#FF5A00', height: 2 },
                          }}
                        >
                          <Tab icon={<Visibility fontSize="small" />} iconPosition="start" label={`Most Viewed (${mostViewedData?.data?.length || 0})`} />
                          <Tab icon={<TrendingUpIcon fontSize="small" />} iconPosition="start" label={`Most Sold (${highSaleData?.data?.length || 0})`} />
                          <Tab label="Low Stock" />
                        </Tabs>

                        <TabPanel value={productTab} index={0}>
                          {mostViewedLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress sx={{ color: '#FF5A00' }} /></Box>
                          ) : mostViewedError ? (
                            <Alert severity="error">Failed to load most viewed products.</Alert>
                          ) : (
                            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 440 }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    {mostViewedHeaders.map((header) => <TableCell key={header.key} sx={{ fontWeight: 800, color: '#344054', backgroundColor: '#FFFFFF' }}>{header.label}</TableCell>)}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(mostViewedData?.data || []).map((product: any, index: number) => (
                                    <TableRow key={product.product_id || index} hover>
                                      <TableCell>{product.product_id}</TableCell>
                                      <TableCell><Typography variant="body2" sx={{ fontWeight: 400 }}>{product.product_name}</Typography></TableCell>
                                      <TableCell><Chip label={formatCurrency(product.price)} size="small" variant="outlined" sx={{ color: '#FF5A00', borderColor: '#FFB38A' }} /></TableCell>
                                      <TableCell>{formatNumber(product.total_views)}</TableCell>
                                      <TableCell>{formatNumber(product.total_viewers)}</TableCell>
                                    </TableRow>
                                  ))}
                                  {(mostViewedData?.data || []).length === 0 && (
                                    <TableRow><TableCell colSpan={mostViewedHeaders.length} align="center" sx={{ py: 5 }}>No data available</TableCell></TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </TabPanel>

                        <TabPanel value={productTab} index={1}>
                          {highSaleLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress sx={{ color: '#FF5A00' }} /></Box>
                          ) : highSaleError ? (
                            <Alert severity="error">Failed to load high sale products.</Alert>
                          ) : (
                            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 440 }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    {highSaleHeaders.map((header) => <TableCell key={header.key} sx={{ fontWeight: 800, color: '#344054', backgroundColor: '#FFFFFF' }}>{header.label}</TableCell>)}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(highSaleData?.data || []).map((product: any, index: number) => (
                                    <TableRow key={product.product_id || index} hover>
                                      <TableCell>{product.product_id}</TableCell>
                                      <TableCell><Typography variant="body2" sx={{ fontWeight: 400 }}>{product.product_name}</Typography></TableCell>
                                      <TableCell><Chip label={formatCurrency(product.price)} size="small" variant="outlined" sx={{ color: '#FF5A00', borderColor: '#FFB38A' }} /></TableCell>
                                      <TableCell>{formatNumber(product.total_qty)}</TableCell>
                                      <TableCell>{formatNumber(product.total_buyers)}</TableCell>
                                      <TableCell>{formatDecimal(product.avg_sale_per_day)}</TableCell>
                                    </TableRow>
                                  ))}
                                  {(highSaleData?.data || []).length === 0 && (
                                    <TableRow><TableCell colSpan={highSaleHeaders.length} align="center" sx={{ py: 5 }}>No data available</TableCell></TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </TabPanel>

                        <TabPanel value={productTab} index={2}>
                          <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 500, color: '#344054' }}>Low Stock</Typography>
                            <Typography variant="body2" color="text.secondary">Low-stock analytics are not provided by the current dashboard API.</Typography>
                          </Box>
                        </TabPanel>
                      </CardContent>
                    </Card>

                    {/* Right column */}
                    <Box sx={{ display: 'grid', gridTemplateRows: 'auto', gap: 2, minWidth: 0 }}>
                      {/* Quick Stats */}
                      <Card sx={{ border: '1px solid #E8EAEE', borderRadius: 2, boxShadow: '0 2px 10px rgba(16,24,40,0.04)' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, color: '#344054' }}>
                            <Business sx={{ color: '#FF5A00' }} /> Quick Stats
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, alignItems: 'stretch' }}>
                            <Box sx={{ minWidth: 0, minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 1, py: 1.25, border: '1px solid #EAECF0', borderRadius: 1.5, backgroundColor: '#FFFFFF' }}>
                              <Typography sx={{ fontWeight: 500, fontSize: 16, lineHeight: 1.3, color: '#344054' }}>{formatNumber(dailyTotalOrders)}</Typography>
                              <Typography sx={{ fontSize: 12, lineHeight: 1.3, fontWeight: 400, color: '#667085', mt: 0.5 }}>Total Orders</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0, minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 1, py: 1.25, border: '1px solid #EAECF0', borderRadius: 1.5, backgroundColor: '#FFFFFF' }}>
                              <Typography sx={{ fontWeight: 500, fontSize: 16, lineHeight: 1.3, color: '#344054' }}>{formatNumber(weeklyNewUsersCurrent)}</Typography>
                              <Typography sx={{ fontSize: 12, lineHeight: 1.3, fontWeight: 400, color: '#667085', mt: 0.5 }}>New Customers</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0, minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 1, py: 1.25, border: '1px solid #EAECF0', borderRadius: 1.5, backgroundColor: '#FFFFFF' }}>
                              <Typography sx={{ fontWeight: 500, fontSize: 16, lineHeight: 1.3, color: '#344054' }}>{formatCurrency(monthCurrent)}</Typography>
                              <Typography sx={{ fontSize: 12, lineHeight: 1.3, fontWeight: 400, color: '#667085', mt: 0.5 }}>Monthly Revenue</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0, minHeight: 82, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 1, py: 1.25, border: '1px solid #EAECF0', borderRadius: 1.5, backgroundColor: '#FFFFFF' }}>
                              <Typography sx={{ fontWeight: 500, fontSize: 16, lineHeight: 1.3, color: '#344054' }}>{formatCurrency(quarterCurrent)}</Typography>
                              <Typography sx={{ fontSize: 12, lineHeight: 1.3, fontWeight: 400, color: '#667085', mt: 0.5 }}>Quarter Revenue</Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>

                  <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #EAECF0', display: 'flex', justifyContent: 'space-between', color: '#667085', fontSize: 12 }}>
                    <span>© 2026 BackOffice. All rights reserved.</span>
                    <span>Version 1.0.0</span>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;